import { FC, ReactNode, useState } from 'react';
import { PaymentElement } from '@stripe/react-stripe-js';
import cn from 'classnames';
import dibsCss from 'dibs-css';
import { FormattedMessage } from 'dibs-react-intl';
import CreditCardIcon from 'dibs-icons/exports/legacy/CreditCard';
import BankAccountIcon from 'dibs-icons/exports/legacy/BankAccount';
import { usePaymentMethodSelectContext } from '../PaymentMethodSelectContext';
import { PaymentMethod } from '../../types';
import { getPaymentElementOptions } from './paymentElementOptions';
import { PaymentMethodSelectOption } from '../PaymentMethodSelectOption';
import { PaymentMethodType } from '../../types';

function getPaymentMethodTitle(paymentMethodType: PaymentMethodType): ReactNode {
    switch (paymentMethodType) {
        case 'CREDIT_CARD':
            return (
                <FormattedMessage
                    id="dibs-payment.StripeSinglePaymentMethod.creditCard"
                    defaultMessage="Credit Card"
                />
            );
        case 'ACH':
            return (
                <FormattedMessage
                    id="dibs-payment.StripeSinglePaymentMethod.bankAccounts"
                    defaultMessage="Bank Accounts"
                />
            );
        default:
            return null;
    }
}

function getPaymentMethodIcon(paymentMethodType: PaymentMethodType): ReactNode {
    switch (paymentMethodType) {
        case 'CREDIT_CARD':
            return <CreditCardIcon className={dibsCss.block} />;
        case 'ACH':
            return <BankAccountIcon className={dibsCss.block} />;
        default:
            return null;
    }
}

type Props = {
    paymentMethod: PaymentMethod;
};

export const StripeSinglePaymentMethod: FC<Props> = props => {
    const { paymentMethod } = props;

    const [isStripeElementReady, setIsStripeElementReady] = useState(false);
    const { paymentMethods, hideWallet, selectedPaymentMethod, updateSelectedPaymentMethod } =
        usePaymentMethodSelectContext();

    if (!paymentMethod) {
        return null;
    }

    function handleReady(): void {
        setIsStripeElementReady(true);
    }

    const { paymentMethodType, paymentProcessor } = paymentMethod;

    const isSelected =
        selectedPaymentMethod?.paymentMethodType === paymentMethodType &&
        selectedPaymentMethod?.paymentProcessor === paymentProcessor &&
        !selectedPaymentMethod?.paymentMethodToken;

    function handleChange(): void {
        updateSelectedPaymentMethod({
            paymentMethodType,
            paymentProcessor: 'STRIPE',
        });
    }

    const showForm = isSelected && isStripeElementReady;

    // show form only if there's nothing else in the select component
    const isStandaloneForm = paymentMethods.length === 1 && hideWallet;
    if (isStandaloneForm) {
        return (
            <div className={cn(dibsCss.mtSmall, { [dibsCss.hidden]: !showForm })}>
                <PaymentElement
                    options={getPaymentElementOptions({ isSingleForm: true })}
                    onReady={handleReady}
                />
            </div>
        );
    }

    return (
        <PaymentMethodSelectOption
            isChecked={isSelected}
            onChange={handleChange}
            dataTn={`payment-method-${paymentMethodType}-input`}
            name="payment-method"
            value={paymentMethodType}
            icon={getPaymentMethodIcon(paymentMethodType)}
            label={
                <div className={cn(dibsCss.flexGrow, dibsCss.sassyFontBodyMediumHeavy)}>
                    {getPaymentMethodTitle(paymentMethodType)}
                </div>
            }
            content={
                isSelected ? (
                    <div className={cn(dibsCss.mtSmall, { [dibsCss.hidden]: !showForm })}>
                        <PaymentElement
                            options={getPaymentElementOptions({ isSingleForm: true })}
                            onReady={handleReady}
                        />
                    </div>
                ) : null
            }
        />
    );
};
